import { useTranslation } from "react-i18next";
import { Chip, Stack, Typography, useTheme } from "@mui/material";
import {
  formatAmountWithCurrency,
  normalizeAccount,
} from "../../../../../helpers/financialImpact";
import {
  useAccountTypeLabels,
  useFlowColor,
  useFlowLabels,
} from "./components";

/**
 * Resumen de una cuenta registrada. Lo comparten el modo edicion y la vista de
 * solo lectura para que ambas muestren exactamente lo mismo.
 */
export const AccountTitle = ({ account, currency }) => {
  const theme = useTheme();
  const flowLabels = useFlowLabels();
  const typeLabels = useAccountTypeLabels();
  const flowColor = useFlowColor();

  const normalized = normalizeAccount(account);

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          label={flowLabels[normalized.flow]}
          sx={{
            fontSize: 11,
            height: 20,
            color: theme.palette.white.main,
            backgroundColor: flowColor(normalized.flow),
          }}
        />
        <Typography fontSize={12} color={theme.palette.blueGray500.main}>
          {typeLabels[normalized.account_type]}
        </Typography>
        {normalized.amount !== null && (
          <Typography
            fontSize={13}
            fontWeight={700}
            color={flowColor(normalized.flow)}
          >
            {formatAmountWithCurrency(normalized.amount, currency)}
          </Typography>
        )}
      </Stack>
      <Typography
        fontSize={14}
        fontWeight={600}
        color={theme.palette.blueGray600.main}
      >
        {[normalized.institution, normalized.account_number]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
    </Stack>
  );
};

export const AccountDetails = ({ account }) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const normalized = normalizeAccount(account);
  if (!normalized.holder && !normalized.details) return null;

  return (
    <Stack spacing={0.5}>
      {normalized.holder && (
        <Typography
          fontSize={14}
          fontWeight={600}
          color={theme.palette.blueGray600.main}
        >
          {t("financialImpact.holder")}:
          <Typography
            component="span"
            fontSize={13}
            fontWeight={400}
            color={theme.palette.oxfordBlue.main}
          >
            {` ${normalized.holder}`}
          </Typography>
        </Typography>
      )}
      {normalized.details && (
        <Typography
          fontSize={14}
          fontWeight={600}
          color={theme.palette.blueGray600.main}
        >
          {t("financialImpact.details")}:
          <Typography
            component="span"
            fontSize={12}
            fontWeight={400}
            color={theme.palette.oxfordBlue.main}
            fontStyle="italic"
          >
            {` ${normalized.details}`}
          </Typography>
        </Typography>
      )}
    </Stack>
  );
};
